import { Header, HoverableInfoIcon } from 'components/patterns'
import PropTypes from 'prop-types'
import React from 'react'
import strings from 'utils/strings'
import { ACTIVE, DESIGN } from 'utils/constants'
import {
  isCollectionCompatibleWithVehicle,
  isLegacyCollection,
  formatVehicleTypeAndQFCode,
  formatEcuAppVariants,
} from './collectionCompatibility'
import { TooltipLabel, TooltipWrapper } from './Search.style'
import {
  CollectionIcon,
  ContentWrapper,
  StyledCard,
  Title,
  TitleWrapper,
  ViewCollectionButton,
  ViewCollectionButtonWrapper,
  StatusText,
  VehicleTypeRow,
  EcuVariantRow,
  CollectionNumber,
  CollectionNumberRow,
  LegacyWarningIcon,
  CardMetaRow,
} from './Card.style'

/**
 * Returns a human-readable status label for a collection.
 * API returns values like "ACTIVE", "DESIGN", "OBSOLETE" (capitalised or upper-case).
 * We normalise to title-case for display.
 */
const getStatusDisplayText = (rawStatus) => {
  if (!rawStatus) return DESIGN
  return rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase()
}

const Card = ({ collection, handleSelection, className, vehicle }) => {
  const {
    name,
    description,
    partNumber,
    status,
    vehicleTypes,
    mdlDbCd,
    varMinCompat,
    xmc4AppVariant,
  } = collection

  // ---- Compatibility -------------------------------------------------------
  const rawStatus = status || DESIGN
  const normalizedStatus = getStatusDisplayText(rawStatus)
  const isActiveStatus = normalizedStatus === ACTIVE

  const compatibilityResult = vehicle
    ? isCollectionCompatibleWithVehicle(collection, vehicle)
    : { compatible: true, isLegacy: false }

  const legacy = isLegacyCollection(collection)
  const isCompatible = isActiveStatus && compatibilityResult.compatible

  // ---- Display values -------------------------------------------------------
  const vehicleTypeLine = formatVehicleTypeAndQFCode(mdlDbCd, vehicleTypes)
  const ecuVariantDisplay = formatEcuAppVariants(varMinCompat, xmc4AppVariant)

  const getIncompatibilityTooltip = () => {
    if (!isActiveStatus) return strings.collection_status_not_active
    return strings.collection_incompatible_tooltip
  }

  return (
    <StyledCard isCompatible={isCompatible} className={className}>
      <TitleWrapper>
        <Title>
          <CollectionIcon icon="use-collection" />
          <Header type="h3">{name}</Header>
        </Title>
        {/* Bug 538201: Status always shown — display-only */}
        <StatusText>{normalizedStatus}</StatusText>
      </TitleWrapper>

      <CardMetaRow>
        <CollectionNumberRow>
          {partNumber && <CollectionNumber>{partNumber}</CollectionNumber>}
          {legacy && (
            <LegacyWarningIcon
              icon="alert_exclamation_triangle_solid"
              width={16}
              height={14}
            />
          )}
          {!legacy && vehicleTypeLine && (
            <VehicleTypeRow>{vehicleTypeLine}</VehicleTypeRow>
          )}
        </CollectionNumberRow>
        {ecuVariantDisplay && (
          <EcuVariantRow>{ecuVariantDisplay}</EcuVariantRow>
        )}
      </CardMetaRow>

      <ContentWrapper>{description}</ContentWrapper>

      <ViewCollectionButtonWrapper>
        {isCompatible ? (
          <ViewCollectionButton onClick={handleSelection}>
            {strings.select_collection}
          </ViewCollectionButton>
        ) : (
          <TooltipWrapper>
            <TooltipLabel>{strings.feature_incompatible}</TooltipLabel>
            <HoverableInfoIcon
              tooltipPosition="left"
              centerRight
              tooltip={getIncompatibilityTooltip()}
              overlayStyle={{ maxWidth: '320px' }}
            />
          </TooltipWrapper>
        )}
      </ViewCollectionButtonWrapper>
    </StyledCard>
  )
}

Card.propTypes = {
  collection: PropTypes.shape({
    name: PropTypes.string,
    partNumber: PropTypes.string,
    description: PropTypes.string,
    status: PropTypes.string,
    vehicleTypes: PropTypes.array,
    mdlDbCd: PropTypes.string,
    varMinCompat: PropTypes.string,
    xmc4AppVariant: PropTypes.string,
  }),
  handleSelection: PropTypes.func,
  className: PropTypes.string,
  vehicle: PropTypes.shape({
    subType: PropTypes.string,
    dbCodes: PropTypes.arrayOf(PropTypes.string),
    buildDate: PropTypes.string,
    modules: PropTypes.array,
  }),
}

export default Card
